/**
 * XPEN-2661: Aderant header defaults (AP type, jurisdiction, payment term)
 * applied from a resolved supplier when the user changes the supplier on the
 * invoice / transaction form. Extraction-time population is handled server-side.
 */

import { shouldResetSupplierDefaultsOnPayeeChange } from './invoice-status';

// 0 and false are intentionally treated as real values; revisit if a numeric or boolean field gets wired through.
const isBlank = (value) => [undefined, null, ''].includes(value);

/**
 * Whether picking a supplier should merge the Aderant header defaults onto the
 * invoice: only for Aderant tenants and only while the invoice is still
 * editable (pre-Need_Approval). Shared by invoice.js and transaction.js so the
 * gate lives in one tested place.
 *
 * @param {boolean} isAderant - Result of Utils.isExportFormat(data, 'aderant').
 * @param {{ status?: string } | null | undefined} invoice
 * @returns {boolean}
 */
export function shouldApplySupplierHeaderDefaults(isAderant, invoice) {
  return Boolean(isAderant) && shouldResetSupplierDefaultsOnPayeeChange(invoice);
}

/**
 * Returns the invoice json with the supplier's Aderant header ids overwritten
 * from the chosen supplier — blanked when the supplier has no default, so the
 * form always reflects the newly picked vendor.
 *
 * Synced Aderant suppliers store these ids as the `{value,label}` option shape
 * (XPEN-2661); legacy / not-yet-resynced suppliers still hold a scalar code.
 * Callers MUST `await labelizeHeaderIds(json)` on the result before persisting
 * — it resolves any remaining scalar into `{value,label}` so the form renders
 * the human-readable label rather than the raw code (objects pass through
 * untouched). The export adapter normalises both shapes.
 *
 * @param {object | null | undefined} json - The invoice json.
 * @param {object | null | undefined} supplier - The resolved supplier document.
 * @returns {{ json: object, changed: boolean }}
 */
export function resolveSupplierHeaderDefaults(json, supplier) {
  if (!json || !supplier) {
    return { json, changed: false };
  }

  const next = { ...json };
  let changed = false;

  const apply = (key, value) => {
    const target = isBlank(value) ? '' : value;
    if (next[key] !== target) {
      next[key] = target;
      changed = true;
    }
  };

  apply('APTypeId', supplier.apTypeId);
  apply('JurisdictionId', supplier.jurisdictionId);
  apply('PaymentTermId', supplier.paymentTermId);

  return { json: next, changed };
}
